import { API_BASE_URL, getAuthHeaders } from "../config";
import {
  CustomerDto,
  CustomerDetailDto,
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerAnalyticsDto,
  TierProgressResponseDto,
  PagedResult,
  TransactionDto,
  TransactionDetailDto,
  AwardPointsDto,
  RedeemPointsDto,
  ManualAdjustmentDto,
  LoyaltyProgramDetailDto,
  CreateLoyaltyProgramDto,
  UpdateLoyaltyProgramDto,
  LoyaltyAnalyticsDto,
  PointsCalculationRequest,
  PointsCalculationResponse,
  PreviewRedemptionRequest,
  PreviewRedemptionResponse,
  LoyaltyTierDto,
  // Reward DTOs
  RewardDto,
  RewardDetailDto,
  CreateRewardDto,
  UpdateRewardDto,
  AvailableRewardsResponse,
  PointsReminderRequestDto,
  PointsReminderResponseDto,
  TierProgressNotificationRequestDto,
  TierProgressNotificationResponseDto,
  PromotionalNotificationRequestDto,
  PromotionalNotificationResponseDto,
  TimeSeriesAnalyticsDto,
  LoyaltyTimeseriesResponse,
  EngagementTrendResponse,
  ErrorResponse,
  TransactionType,
} from "../types/backend-api.types";

// Utility function for handling API responses with better error diagnostics
const handleApiResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    // Attempt to parse JSON error body, fall back to text if parsing fails
    let errorMessage = ``;
    try {
      const errorData: ErrorResponse = await response.json();
      errorMessage = errorData?.message || JSON.stringify(errorData);
    } catch (jsonErr) {
      try {
        errorMessage = await response.text();
      } catch (textErr) {
        errorMessage = response.statusText || `HTTP ${response.status}`;
      }
    }

    // Log helpful diagnostics to the console to aid debugging in dev
    // eslint-disable-next-line no-console
    console.error(
      `[API] ${response.url} -> HTTP ${response.status} ${response.statusText}:`,
      errorMessage
    );

    switch (response.status) {
      case 400:
        throw new Error(`Validation Error: ${errorMessage}`);
      case 401:
        throw new Error("Authentication required");
      case 404:
        throw new Error(`Resource not found: ${errorMessage}`);
      case 500:
        throw new Error(`Server error: ${errorMessage}`);
      default:
        throw new Error(`HTTP ${response.status}: ${errorMessage}`);
    }
  }

  // If response has no content, return undefined cast
  const text = await response.text();
  try {
    return text ? (JSON.parse(text) as T) : (undefined as unknown as T);
  } catch (err) {
    // If parsing fails, return the raw text as any
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return text as unknown as T;
  }
};

// Customer API Functions
export const customerApi = {
  // Get paginated customers list (Supports usage without restaurantId if token based)
  async getCustomers(
    token: string,
    page: number = 1,
    size: number = 50,
    restaurantId?: number
  ): Promise<PagedResult<CustomerDto>> {
    let url = `${API_BASE_URL}/customers?page=${page}&size=${size}`;
    if (restaurantId) {
      url = `${API_BASE_URL}/customers/restaurant/${restaurantId}?page=${page}&size=${size}`;
    }

    const response = await fetch(url, { headers: getAuthHeaders(token) });
    return handleApiResponse<PagedResult<CustomerDto>>(response);
  },

  // Get customer details by ID
  async getCustomerDetails(
    token: string,
    customerId: string
  ): Promise<CustomerDetailDto> {
    const response = await fetch(`${API_BASE_URL}/customers/${customerId}`, {
      headers: getAuthHeaders(token),
    });

    return handleApiResponse<CustomerDetailDto>(response);
  },

  // Create new customer
  async createCustomer(
    token: string,
    customerData: CreateCustomerDto
  ): Promise<CustomerDto> {
    const response = await fetch(`${API_BASE_URL}/customers`, {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(customerData),
    });

    if (!response.ok) {
      // Let handleApiResponse produce a better diagnostic log and throw
      return handleApiResponse<CustomerDto>(response);
    }

    // For OK responses return parsed body
    return handleApiResponse<CustomerDto>(response);
  },

  // Update customer - attempt PUT, fall back to PATCH if server rejects PUT
  async updateCustomer(
    token: string,
    customerId: string,
    customerData: UpdateCustomerDto
  ): Promise<CustomerDto> {
    const url = `${API_BASE_URL}/customers/${customerId}`;

    // Try PUT first (server may expect full DTO)
    try {
      const putResp = await fetch(url, {
        method: "PUT",
        headers: getAuthHeaders(token),
        body: JSON.stringify(customerData),
      });

      if (putResp.ok) {
        return handleApiResponse<CustomerDto>(putResp);
      }

      // If server responds 405 Method Not Allowed, try PATCH as a fallback for partial updates
      if (putResp.status === 405) {
        const patchResp = await fetch(url, {
          method: "PATCH",
          headers: getAuthHeaders(token),
          body: JSON.stringify(customerData),
        });

        return handleApiResponse<CustomerDto>(patchResp);
      }

      // For other non-ok statuses let the shared handler surface a helpful error
      return handleApiResponse<CustomerDto>(putResp);
    } catch (err) {
      // Network-level or unexpected errors bubble up
      throw err;
    }
  },

  // Delete customer
  async deleteCustomer(token: string, customerId: string): Promise<void> {
    const url = `${API_BASE_URL}/customers/${customerId}`;
    // Diagnostic logging to help trace backend issues (safe to keep in dev)
    try {
      // Build headers for logging but avoid printing the raw token
      const headersForLog = { ...getAuthHeaders(token) } as Record<
        string,
        string
      >;
      if (headersForLog.Authorization) {
        headersForLog.Authorization = headersForLog.Authorization.replace(
          /Bearer\s+.+$/,
          "Bearer <redacted>"
        );
      }
      // eslint-disable-next-line no-console
      console.debug("[customerApi.deleteCustomer] DELETE", url, headersForLog);

      const response = await fetch(url, {
        method: "DELETE",
        headers: getAuthHeaders(token),
      });

      if (!response.ok) {
        // Try to parse JSON body safely
        let bodyText = "";
        try {
          bodyText = await response.text();
        } catch (e) {
          bodyText = `<failed to read body: ${e}>`;
        }
        // eslint-disable-next-line no-console
        console.error(
          `[customerApi.deleteCustomer] Failed DELETE ${url} -> ${response.status} ${response.statusText}:`,
          bodyText
        );

        // Attempt to parse meaningful error message
        try {
          const errorData: ErrorResponse = JSON.parse(bodyText || "{}");
          throw new Error(errorData.message || `HTTP ${response.status}`);
        } catch (parseErr) {
          throw new Error(`HTTP ${response.status}: ${bodyText}`);
        }
      }
      // eslint-disable-next-line no-console
      console.debug(`[customerApi.deleteCustomer] DELETE success ${url}`);
    } catch (err) {
      // Re-throw after logging
      // eslint-disable-next-line no-console
      console.error(
        "[customerApi.deleteCustomer] Error while deleting customer:",
        err
      );
      throw err;
    }
  },

  // Search customers (supports both legacy and new signatures)
  async searchCustomers(
    token: string,
    arg2: string | number,
    arg3?: string | number
  ): Promise<CustomerDto[]> {
    let query: string;
    let restaurantId: number | undefined;

    if (typeof arg2 === 'number') {
      // Legacy: (token, restaurantId, query)
      restaurantId = arg2;
      query = typeof arg3 === 'string' ? arg3 : '';
    } else {
      // New: (token, query, restaurantId?)
      query = arg2 as string;
      restaurantId = typeof arg3 === 'number' ? arg3 : undefined;
    }

    if (query.length < 2) {
      return [];
    }

    let url = `${API_BASE_URL}/customers/search?query=${encodeURIComponent(query)}`;
    if (restaurantId) {
       url = `${API_BASE_URL}/customers/search?restaurantId=${restaurantId}&query=${encodeURIComponent(query)}`;
    }

    const response = await fetch(url, { headers: getAuthHeaders(token) });

    // Handle potential paged result wrapper from backend
    const data = await handleApiResponse<any>(response);
    if (data && data.items && Array.isArray(data.items)) {
       return data.items;
    }
    return Array.isArray(data) ? data : [];
  },

  // Get customer transactions
  async getCustomerTransactions(
    token: string,
    customerId: string,
    page: number = 1,
    size: number = 50
  ): Promise<PagedResult<TransactionDto>> {
    const response = await fetch(
      `${API_BASE_URL}/customers/${customerId}/transactions?page=${page}&size=${size}`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<PagedResult<TransactionDto>>(response);
  },

  // Get customer analytics
  async getCustomerAnalytics(
    token: string,
    customerId: string
  ): Promise<CustomerAnalyticsDto> {
    try {
      const url = `${API_BASE_URL}/customers/${customerId}/analytics`;
      const response = await fetch(url, {
        headers: getAuthHeaders(token),
      });

      if (!response.ok) {
        console.error(
          `[customerApi.getCustomerAnalytics] Failed ${response.status} ${response.statusText} for customer ${customerId}`
        );

        // Return default analytics on 404 instead of throwing
        if (response.status === 404) {
          console.warn(
            `⚠️ Analytics endpoint not found for customer ${customerId}, returning default values`
          );
          return {
            customerId,
            fullName: "",
            totalVisits: 0,
            totalSpent: 0,
            pointsEarned: 0,
            pointsRedeemed: 0,
            currentBalance: 0,
            lastVisit: new Date(),
            averageOrderValue: 0,
            daysSinceLastVisit: 0,
          };
        }
      }

      return handleApiResponse<CustomerAnalyticsDto>(response);
    } catch (error) {
      console.error(
        `[customerApi.getCustomerAnalytics] Error for customer ${customerId}:`,
        error
      );
      // Return default analytics instead of throwing
      return {
        customerId,
        fullName: "",
        totalVisits: 0,
        totalSpent: 0,
        pointsEarned: 0,
        pointsRedeemed: 0,
        currentBalance: 0,
        lastVisit: new Date(),
        averageOrderValue: 0,
        daysSinceLastVisit: 0,
      };
    }
  },

  // Get customer tier progress
  async getCustomerTierProgress(
    token: string,
    customerId: string
  ): Promise<TierProgressResponseDto> {
    const response = await fetch(
      `${API_BASE_URL}/customers/${customerId}/tier-progress`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<TierProgressResponseDto>(response);
  },
};

// Transaction API Functions
export const transactionApi = {
  // Get paginated transactions
  async getTransactions(
    token: string,
    restaurantId: number,
    page: number = 1,
    size: number = 50,
    type?: TransactionType
  ): Promise<PagedResult<TransactionDto>> {
    let url = `${API_BASE_URL}/loyaltytransactions/${restaurantId}?page=${page}&size=${size}`;
    if (type !== undefined) {
      url += `&type=${type}`;
    }

    const response = await fetch(url, {
      headers: getAuthHeaders(token),
    });

    return handleApiResponse<PagedResult<TransactionDto>>(response);
  },

  // Get transaction details
  async getTransactionDetails(
    token: string,
    transactionId: string
  ): Promise<TransactionDetailDto> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltytransactions/transaction/${transactionId}`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<TransactionDetailDto>(response);
  },

  // Award points to customer
  async awardPoints(
    token: string,
    awardData: AwardPointsDto
  ): Promise<TransactionDto> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltytransactions/award-points`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
        body: JSON.stringify(awardData),
      }
    );

    return handleApiResponse<TransactionDto>(response);
  },

  // Redeem points
  async redeemPoints(
    token: string,
    redeemData: RedeemPointsDto
  ): Promise<TransactionDto> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltytransactions/redeem-points`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
        body: JSON.stringify(redeemData),
      }
    );

    return handleApiResponse<TransactionDto>(response);
  },

  // Redeem points for direct discount
  async redeemPointsForDiscount(
    token: string,
    redeemData: {
      customerId: string;
      pointsToRedeem: number;
      orderId: number; // Required: Order ID this discount is linked to
      description?: string;
      createdBy?: string;
    }
  ): Promise<{
    transactionId: string;
    pointsRedeemed: number;
    discountValue: number;
    remainingPoints: number;
    transactionDate: string;
    orderId: number;
  }> {
    const response = await fetch(
      `${API_BASE_URL}/LoyaltyTransactions/redeem-points-for-discount`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
        body: JSON.stringify(redeemData),
      }
    );

    return handleApiResponse(response);
  },

  // Manual points adjustment
  async manualAdjustment(
    token: string,
    adjustmentData: ManualAdjustmentDto
  ): Promise<TransactionDto> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltytransactions/manual-adjustment`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
        body: JSON.stringify(adjustmentData),
      }
    );

    return handleApiResponse<TransactionDto>(response);
  },

  // Reverse transaction
  async reverseTransaction(
    token: string,
    transactionId: string
  ): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltytransactions/${transactionId}`,
      {
        method: "DELETE",
        headers: getAuthHeaders(token),
      }
    );

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.message);
    }
  },

  // Fallback reverse via POST /loyaltytransactions/{id}/reverse
  async reverseTransactionFallback(
    token: string,
    transactionId: string
  ): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltytransactions/${transactionId}/reverse`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
      }
    );

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.message);
    }
  },
};

// Loyalty Program API Functions
export const loyaltyProgramApi = {
  // Get loyalty program details
  async getLoyaltyProgram(
    token: string,
    restaurantId: number
  ): Promise<LoyaltyProgramDetailDto> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltyprograms/${restaurantId}`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<LoyaltyProgramDetailDto>(response);
  },

  // Create loyalty program
  async createLoyaltyProgram(
    token: string,
    programData: CreateLoyaltyProgramDto
  ): Promise<LoyaltyProgramDetailDto> {
    // Debug logging to help diagnose 400 Bad Request errors from backend
    try {
      // eslint-disable-next-line no-console
      console.debug(
        "[backend-loyalty.api] POST /loyaltyprograms payload:",
        programData
      );
      // eslint-disable-next-line no-console
      console.debug(
        "[backend-loyalty.api] POST /loyaltyprograms headers:",
        getAuthHeaders(token)
      );
    } catch (err) {
      // ignore logging errors
    }

    const response = await fetch(`${API_BASE_URL}/loyaltyprograms`, {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(programData),
    });

    return handleApiResponse<LoyaltyProgramDetailDto>(response);
  },

  // Update loyalty program
  async updateLoyaltyProgram(
    token: string,
    programId: string,
    programData: UpdateLoyaltyProgramDto
  ): Promise<LoyaltyProgramDetailDto> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltyprograms/${programId}`,
      {
        method: "PUT",
        headers: getAuthHeaders(token),
        body: JSON.stringify(programData),
      }
    );

    return handleApiResponse<LoyaltyProgramDetailDto>(response);
  },

  // Delete loyalty program
  async deleteLoyaltyProgram(token: string, programId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltyprograms/${programId}`,
      {
        method: "DELETE",
        headers: getAuthHeaders(token),
      }
    );

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.message);
    }
  },

  // Get loyalty analytics
  async getLoyaltyAnalytics(
    token: string,
    restaurantId: number
  ): Promise<LoyaltyAnalyticsDto> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltyprograms/${restaurantId}/analytics`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<LoyaltyAnalyticsDto>(response);
  },

  // Get loyalty tiers
  async getLoyaltyTiers(
    token: string,
    restaurantId: number
  ): Promise<LoyaltyTierDto[]> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltyprograms/${restaurantId}/tiers`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<LoyaltyTierDto[]>(response);
  },

  // Update loyalty tiers
  async updateLoyaltyTiers(
    token: string,
    restaurantId: number,
    tiers: LoyaltyTierDto[]
  ): Promise<LoyaltyTierDto[]> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltyprograms/${restaurantId}/tiers`,
      {
        method: "PUT",
        headers: getAuthHeaders(token),
        body: JSON.stringify(tiers),
      }
    );

    return handleApiResponse<LoyaltyTierDto[]>(response);
  },

  // Get time-series analytics data
  async getTimeSeriesAnalytics(
    token: string,
    restaurantId: number,
    timeRange: string = "30days"
  ): Promise<TimeSeriesAnalyticsDto> {
    // Convert frontend friendly "30days" to backend expected "30d"
    const apiTimeRange = timeRange.replace("days", "d").replace("day", "d");

    const response = await fetch(
      `${API_BASE_URL}/loyaltyprograms/${restaurantId}/analytics/timeseries?timeRange=${apiTimeRange}`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<TimeSeriesAnalyticsDto>(response);
  },

  // Reward Catalog Endpoints
  async getRewards(
    token: string,
    restaurantId: number,
    includeInactive: boolean = false
  ): Promise<RewardDto[]> {
    const response = await fetch(
      `${API_BASE_URL}/RewardCatalog/${restaurantId}?includeInactive=${includeInactive}`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<RewardDto[]>(response);
  },

  async getRewardDetails(
    token: string,
    rewardId: string
  ): Promise<RewardDetailDto> {
    const response = await fetch(
      `${API_BASE_URL}/RewardCatalog/reward/${rewardId}`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<RewardDetailDto>(response);
  },

  async createReward(
    token: string,
    createData: CreateRewardDto
  ): Promise<RewardDto> {
    const response = await fetch(`${API_BASE_URL}/RewardCatalog`, {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(createData),
    });

    return handleApiResponse<RewardDto>(response);
  },

  async updateReward(
    token: string,
    rewardId: string,
    updateData: UpdateRewardDto
  ): Promise<RewardDto> {
    const response = await fetch(`${API_BASE_URL}/RewardCatalog/${rewardId}`, {
      method: "PUT",
      headers: getAuthHeaders(token),
      body: JSON.stringify(updateData),
    });

    return handleApiResponse<RewardDto>(response);
  },

  async deleteReward(token: string, rewardId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/RewardCatalog/${rewardId}`, {
      method: "DELETE",
      headers: getAuthHeaders(token),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.message);
    }
  },

  async toggleRewardStatus(
    token: string,
    rewardId: string
  ): Promise<RewardDto> {
    const response = await fetch(
      `${API_BASE_URL}/RewardCatalog/${rewardId}/toggle-status`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<RewardDto>(response);
  },

  async getAvailableRewardsForCustomer(
    token: string,
    customerId: string
  ): Promise<AvailableRewardsResponse> {
    const response = await fetch(
      `${API_BASE_URL}/RewardCatalog/${customerId}/available-rewards`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<AvailableRewardsResponse>(response);
  },
};

// Points Calculation API Functions
export const pointsApi = {
  // Calculate points for an order
  async calculatePoints(
    token: string,
    calculationData: PointsCalculationRequest
  ): Promise<PointsCalculationResponse> {
    const response = await fetch(`${API_BASE_URL}/points/calculate`, {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(calculationData),
    });

    return handleApiResponse<PointsCalculationResponse>(response);
  },
  async previewRedemption(
    token: string,
    previewData: PreviewRedemptionRequest
  ): Promise<PreviewRedemptionResponse> {
    const response = await fetch(
      `${API_BASE_URL}/PointsCalculation/preview-redemption`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
        body: JSON.stringify(previewData),
      }
    );

    return handleApiResponse<PreviewRedemptionResponse>(response);
  },
  // Trigger point expiry processing (server-side job)
  async processExpiry(token: string, restaurantId?: number): Promise<any> {
    const url = `${API_BASE_URL}/PointsCalculation/process-expiry`;
    const response = await fetch(url, {
      method: "POST",
      headers: getAuthHeaders(token),
      body: restaurantId ? JSON.stringify({ restaurantId }) : undefined,
    });

    return handleApiResponse<any>(response);
  },
};

// Notification API Functions
export const notificationApi = {
  // Send points reminder
  async sendPointsReminder(
    restaurantId: number,
    reminderData: PointsReminderRequestDto,
    token?: string
  ): Promise<PointsReminderResponseDto> {
    const response = await fetch(
      `${API_BASE_URL}/notifications/points-reminder?restaurantId=${restaurantId}`,
      {
        method: "POST",
        headers: getAuthHeaders(token), // Auth temporarily disabled for notifications
        body: JSON.stringify(reminderData),
      }
    );

    return handleApiResponse<PointsReminderResponseDto>(response);
  },

  // Send tier progress notification
  async sendTierProgressNotification(
    restaurantId: number,
    notificationData: TierProgressNotificationRequestDto,
    token?: string
  ): Promise<TierProgressNotificationResponseDto> {
    const response = await fetch(
      `${API_BASE_URL}/notifications/tier-progress?restaurantId=${restaurantId}`,
      {
        method: "POST",
        headers: getAuthHeaders(token), // Auth temporarily disabled for notifications
        body: JSON.stringify(notificationData),
      }
    );

    return handleApiResponse<TierProgressNotificationResponseDto>(response);
  },

  // Send promotional notification
  async sendPromotionalNotification(
    restaurantId: number,
    notificationData: PromotionalNotificationRequestDto,
    token?: string
  ): Promise<PromotionalNotificationResponseDto> {
    const response = await fetch(
      `${API_BASE_URL}/notifications/promotional?restaurantId=${restaurantId}`,
      {
        method: "POST",
        headers: getAuthHeaders(token), // Auth temporarily disabled for notifications
        body: JSON.stringify(notificationData),
      }
    );

    return handleApiResponse<PromotionalNotificationResponseDto>(response);
  },

  // ========== Birthday Notification Methods ==========
  // Check for upcoming birthdays and create notifications
  async checkUpcomingBirthdays(
    restaurantId: number,
    token?: string
  ): Promise<any> {
    try {
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      const response = await fetch(
        `${API_BASE_URL}/notifications/birthdays/check?restaurantId=${restaurantId}`,
        {
          method: "GET",
          headers: getAuthHeaders(token),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Graceful fallback - birthday feature may not be implemented on backend yet
        return { todayBirthdays: [], upcomingBirthdays: [] };
      }

      return handleApiResponse<any>(response);
    } catch (error) {
      // Graceful error handling - feature may not be implemented yet
      return { todayBirthdays: [], upcomingBirthdays: [] };
    }
  },

  // Get birthday notifications for owner/manager
  async getBirthdayNotifications(
    restaurantId: number,
    token?: string,
    daysInAdvance: number = 7
  ): Promise<any> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/notifications/birthdays?restaurantId=${restaurantId}&daysInAdvance=${daysInAdvance}`,
        {
          method: "GET",
          headers: getAuthHeaders(token),
        }
      );

      if (!response.ok) {
        return [];
      }

      return handleApiResponse<any>(response);
    } catch (error) {
      return [];
    }
  },

  // Send birthday notification/reminder to customer
  async sendBirthdayNotification(
    restaurantId: number,
    customerId: string,
    notificationData: any,
    token?: string
  ): Promise<any> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/notifications/birthdays/send?restaurantId=${restaurantId}&customerId=${customerId}`,
        {
          method: "POST",
          headers: getAuthHeaders(token),
          body: JSON.stringify(notificationData),
        }
      );

      if (!response.ok) {
        return {
          success: false,
          message: "Failed to send birthday notification",
        };
      }

      return handleApiResponse<any>(response);
    } catch (error) {
      return { success: false, message: String(error) };
    }
  },

  // Mark birthday notification as sent to customer
  async markBirthdayAsSent(
    restaurantId: number,
    customerId: string,
    token?: string
  ): Promise<any> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/notifications/birthdays/mark-sent?restaurantId=${restaurantId}&customerId=${customerId}`,
        {
          method: "POST",
          headers: getAuthHeaders(token),
        }
      );

      if (!response.ok) {
        return { success: false };
      }

      return handleApiResponse<any>(response);
    } catch (error) {
      return { success: false };
    }
  },
};

// Promotions API Functions
export const promotionsApi = {
  // Get campaigns (paged)
  async getCampaigns(
    token: string | undefined,
    restaurantId: number,
    page: number = 1,
    size: number = 50
  ) {
    const url = `${API_BASE_URL}/promotions?page=${page}&size=${size}`;
    try {
      const res = await fetch(url, { headers: getAuthHeaders(token) });
      return await handleApiResponse<PagedResult<any>>(res);
    } catch (err) {
      console.error(`[promotionsApi] getCampaigns failed:`, err);
      return {
        items: [],
        totalCount: 0,
        pageNumber: page,
        pageSize: size,
        totalPages: 0,
        hasPreviousPage: page > 1,
        hasNextPage: false,
      } as PagedResult<any>;
    }
  },

  async getCampaignById(token: string | undefined, campaignId: string) {
    const response = await fetch(
      `${API_BASE_URL}/promotions/campaign/${campaignId}`,
      { headers: getAuthHeaders(token) }
    );

    return handleApiResponse<any>(response);
  },

  async createCampaign(token: string | undefined, campaignData: any) {
    const response = await fetch(`${API_BASE_URL}/promotions`, {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(campaignData),
    });

    return handleApiResponse<any>(response);
  },

  async updateCampaign(
    token: string | undefined,
    campaignId: string,
    campaignData: any
  ) {
    const response = await fetch(`${API_BASE_URL}/promotions/${campaignId}`, {
      method: "PUT",
      headers: getAuthHeaders(token),
      body: JSON.stringify(campaignData),
    });

    return handleApiResponse<any>(response);
  },

  async deleteCampaign(token: string | undefined, campaignId: string) {
    const response = await fetch(`${API_BASE_URL}/promotions/${campaignId}`, {
      method: "DELETE",
      headers: getAuthHeaders(token),
    });

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.message);
    }
  },

  // Actions
  /**
   * Dispatch a campaign via WhatsApp bulk send.
   * Calls POST /api/whatsapp/send-bulk.
   *
   * Requires either:
   *   - templateId (local DB Guid) — looked up by the service to get message content
   *   - customMessage — raw text sent directly (no local template needed)
   *
   * Audience:
   *   - customerIds — specific customer Guids (takes priority)
   *   - filterCriteria — e.g. { loyaltyTier: "Gold" } (empty = all customers)
   */
  async sendCampaignViaWhatsApp(
    token: string | undefined,
    campaign: {
      campaignId?: string;
      name?: string;
      templateId?: string | null;
      templateName?: string | null;
      templateLang?: string | null;
      templateVariables?: Record<string, string> | null;
      phoneNumbers?: string[];
      customerIds?: string[];
      filterCriteria?: Record<string, any>;
      customMessage?: string | null;
      customSubject?: string | null;
    }
  ) {
    // Helper: a GUID looks like xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const isGuid = (s: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

    // Resolve phone numbers: explicit phoneNumbers OR customerIds that are NOT GUIDs
    // (targetFilters.customerIds stores phone numbers when built from targetPhones)
    const rawPhones = [
      ...(campaign.phoneNumbers ?? []),
      ...(campaign.customerIds ?? []).filter((id) => !isGuid(id)),
    ];
    // Normalize, filter falsy, and deduplicate
    const resolvedPhones: string[] = Array.from(
      new Set(
        rawPhones
          .map((p) => p?.trim())
          .filter(Boolean)
      )
    );

    // Resolve true GUID customer IDs (actual DB customer GUIDs)
    const resolvedCustomerGuids: string[] = (campaign.customerIds ?? []).filter(isGuid);

    // ── Path 1: WhatsApp template send (messages/send-bulk) ──────────────────
    // Requires: templateName + at least one phone number
    if (campaign.templateName && resolvedPhones.length > 0) {
      const headerImageUrl = campaign.templateVariables?.["__header__"] ?? null;
      const bodyEntries = Object.entries(campaign.templateVariables ?? {})
        .filter(([k]) => k !== "__header__")
        .sort(([a], [b]) => a.localeCompare(b));

      const components: object[] = [];
      if (headerImageUrl) {
        components.push({
          type: "header",
          parameters: [{ type: "image", image: { link: headerImageUrl } }],
        });
      }
      if (bodyEntries.length > 0) {
        components.push({
          type: "body",
          parameters: bodyEntries.map(([, v]) => ({ type: "text", text: v })),
        });
      }

      // Recipients: strip non-digits so "+917083951137" → "917083951137"
      const recipients = resolvedPhones.map((p) => p.replace(/\D/g, ""));

      const payload = {
        recipients,
        template: {
          name: campaign.templateName,
          language: campaign.templateLang ?? "en",
          components,
        },
        campaignName: campaign.name ?? "Promotion Campaign",
      };

      const response = await fetch(`${API_BASE_URL}/whatsapp/messages/send-bulk`, {
        method: "POST",
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload),
      });
      return handleApiResponse<any>(response);
    }

    // ── Path 2: Legacy send-bulk with GUID customer IDs or filter criteria ───
    const legacyPayload: Record<string, any> = {};
    if (campaign.templateId) legacyPayload.templateId = campaign.templateId;
    if (campaign.customMessage) legacyPayload.customMessage = campaign.customMessage;
    if (resolvedCustomerGuids.length > 0) legacyPayload.customerIds = resolvedCustomerGuids;
    legacyPayload.filterCriteria = campaign.filterCriteria ?? {};

    const response = await fetch(`${API_BASE_URL}/whatsapp/send-bulk`, {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(legacyPayload),
    });
    return handleApiResponse<any>(response);
  },

  /** @deprecated Use sendCampaignViaWhatsApp instead */
  async sendCampaign(token: string | undefined, campaignId: string) {
    return this.sendCampaignViaWhatsApp(token, { campaignId, name: campaignId });
  },

  /**
   * Trigger a campaign to be sent via the backend campaigns endpoint.
   * Tries POST /api/promotions/{id}/send first, then falls back to
   * POST /api/promotions/campaign/{id}/send, and finally falls back to
   * POST /api/promotions/send-campaign.
   */
  async triggerCampaignSend(token: string | undefined, campaignId: string) {
    // Try primary endpoint
    const primaryUrl = `${API_BASE_URL}/promotions/${campaignId}/send`;
    let response = await fetch(primaryUrl, {
      method: "POST",
      headers: getAuthHeaders(token),
    });

    // If 404, try alternative endpoint path
    if (response.status === 404) {
      const altUrl = `${API_BASE_URL}/promotions/campaign/${campaignId}/send`;
      response = await fetch(altUrl, {
        method: "POST",
        headers: getAuthHeaders(token),
      });
    }

    // If still 404, try the /send-campaign endpoint
    if (response.status === 404) {
      const alt2Url = `${API_BASE_URL}/promotions/send-campaign`;
      response = await fetch(alt2Url, {
        method: "POST",
        headers: getAuthHeaders(token),
        body: JSON.stringify({ campaignId }),
      });
    }

    return handleApiResponse<any>(response);
  },

  async scheduleCampaign(
    token: string | undefined,
    campaignId: string,
    scheduledAt: string
  ) {
    const response = await fetch(
      `${API_BASE_URL}/promotions/${campaignId}/schedule`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
        body: JSON.stringify({ scheduledAt }),
      }
    );

    return handleApiResponse<any>(response);
  },

  async pauseCampaign(token: string | undefined, campaignId: string) {
    const response = await fetch(
      `${API_BASE_URL}/promotions/${campaignId}/pause`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<any>(response);
  },

  async resumeCampaign(token: string | undefined, campaignId: string) {
    const response = await fetch(
      `${API_BASE_URL}/promotions/${campaignId}/resume`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<any>(response);
  },

  async duplicateCampaign(token: string | undefined, campaignId: string) {
    const response = await fetch(
      `${API_BASE_URL}/promotions/${campaignId}/duplicate`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<any>(response);
  },

  // Templates
  async getTemplates(token: string | undefined, restaurantId: number) {
    const response = await fetch(
      `${API_BASE_URL}/promotions/templates/${restaurantId}`,
      { headers: getAuthHeaders(token) }
    );

    return handleApiResponse<any[]>(response);
  },

  async createTemplate(token: string | undefined, templateData: any) {
    const response = await fetch(`${API_BASE_URL}/promotions/templates`, {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(templateData),
    });

    return handleApiResponse<any>(response);
  },

  // Preview reach (returns count and sample customers)
  async previewReach(
    token: string | undefined,
    restaurantId: number,
    filters: any
  ) {
    const response = await fetch(
      `${API_BASE_URL}/promotions/preview-reach?restaurantId=${restaurantId}`,
      {
        method: "POST",
        headers: getAuthHeaders(token),
        body: JSON.stringify(filters),
      }
    );

    return handleApiResponse<any>(response);
  },
};

// Loyalty Analytics API Functions
export const loyaltyAnalyticsApi = {
  // Get loyalty program timeseries analytics
  async getLoyaltyTimeseries(
    token: string,
    restaurantId: number,
    period: string = "daily",
    startDate?: string,
    endDate?: string
  ): Promise<LoyaltyTimeseriesResponse> {
    let url = `${API_BASE_URL}/loyaltyprograms/${restaurantId}/analytics/timeseries?period=${period}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;

    const response = await fetch(url, {
      headers: getAuthHeaders(token),
    });

    return handleApiResponse<LoyaltyTimeseriesResponse>(response);
  },

  // Get complete time series analytics with top performing days
  async getTimeSeriesAnalytics(
    token: string,
    restaurantId: number,
    timeRange: string = "30d",
    startDate?: string,
    endDate?: string
  ): Promise<TimeSeriesAnalyticsDto> {
    let url = `${API_BASE_URL}/loyaltyprograms/${restaurantId}/analytics/timeseries?timeRange=${timeRange}`;

    // Add custom date range if provided
    if (startDate) {
      url += `&startDate=${startDate}`;
    }
    if (endDate) {
      url += `&endDate=${endDate}`;
    }


    const response = await fetch(url, {
      headers: getAuthHeaders(token),
    });

    return handleApiResponse<TimeSeriesAnalyticsDto>(response);
  },

  // Get engagement trend data
  async getEngagementTrendData(
    token: string,
    restaurantId: number,
    period: string = "30d"
  ): Promise<EngagementTrendResponse> {
    const response = await fetch(
      `${API_BASE_URL}/loyaltyanalytics/engagement/${restaurantId}/trend-data?period=${period}`,
      {
        headers: getAuthHeaders(token),
      }
    );

    return handleApiResponse<EngagementTrendResponse>(response);
  },
};
